import { uniqBy } from 'lodash';
import { FormSectionHandler } from '../entity.handler';
import { VendorResponse, IdName, VendorUpdateRequest, DistributorResponse, IdNameState } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, ComboField, CheckListField, IdNameComboValueHandlers, IdNameArrayComboValueHandlers } from '../utils';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    availableEmailProviders?: IdNameState[];
    defaultMapSet?: IdName;
    availableMapSets?: IdName[];
    availableDeviceTypes?: IdName[];
}

export class VendorSettingsSectionHandler implements FormSectionHandler<VendorResponse, EditorValues> {

    type = 'form';
    icon = 'cogs';
    bulk = true;
    readonly = this.admin.user.owner.type === 'vendor';

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    private properties = ['availableEmailProviders', 'defaultMapSet', 'availableMapSets', 'availableDeviceTypes'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.VENDOR.SETTINGS.${key}`, values);
    }

    applyUpdate(entity: VendorResponse, payload: VendorUpdateRequest): Promise<VendorResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateVendor(entity.id, payload));
    }

    async mapEntityToEditor(entity: VendorResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {});
        const distributor = this.readonly ? null : await this.admin.getCached(`getDistributor/${entity.owner.id}`, api => api.accounts.getDistributor(entity.owner.id)).catch(nullAccessDenied);

        const availableEmailProviderIds = (await this.getEmailProviders(entity, distributor)).map(x => x.key);
        result.availableEmailProviders = result.availableEmailProviders.filter(x => availableEmailProviderIds.includes(x.id));

        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: any, values: EditorValues): Promise<any> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, values, entity);
        return Promise.resolve(result);
    }

    async getFields(entity: VendorResponse): Promise<FormBuilderField[]> {

        const distributor = this.readonly ? null : await this.admin.getCached(`getDistributor/${entity.owner.id}`, api => api.accounts.getDistributor(entity.owner.id)).catch(nullAccessDenied);

        return Promise.all([
            this.getEmailProviders(entity, distributor),
            this.getAvailableDeviceTypes(),
            this.getAvailableMapSets(distributor),
        ]).then(([emailProviderValues, deviceTypeValues, mapSetValues]) => {
            deviceTypeValues.sort((a, b) => a.value.localeCompare(b.value));
            return [
                CheckListField('availableEmailProviders', this.translate('FIELDS.EMAILPROVIDERS'), false, emailProviderValues || [], IdNameArrayComboValueHandlers),
                CheckListField('availableMapSets', this.translate('FIELDS.MAPSETS'), true, mapSetValues || [], IdNameArrayComboValueHandlers),
                ComboField('defaultMapSet', this.translate('FIELDS.MAPSET'), true, [{ key: 'default', value: 'Default' }, ...mapSetValues], IdNameComboValueHandlers),
                CheckListField('availableDeviceTypes', this.translate('FIELDS.DEVICETYPES'), false, deviceTypeValues, IdNameArrayComboValueHandlers),
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, ['availableEmailProviders', 'availableMapSets', 'defaultMapSet', 'availableDeviceTypes'], fields));
    }

    
    async getAvailableDeviceTypes(): Promise<{ key: string, value: string }[]> {
        const deviceTypes = await this.admin.getCached(`listDeviceTypes/${this.ownerId}`, api => api.entities.listDeviceTypes(this.ownerId));
        return deviceTypes.items.filter(x => !x.parent).map(x => ({ key: x.id, value: x.name })).sort((a, b) => a.value.localeCompare(b.value));
    }

    async getAvailableMapSets(distibutor: DistributorResponse): Promise<{ key: string, value: string }[]> {
        const mapSets = distibutor ? await this.admin.getCached(`listMapSets/${distibutor.id}/0/100/name/state!=deleted`, api => api.entities.listMapSets(distibutor.id, 0, 100, 'name', 'state!=deleted')).catch(nullAccessDenied) : null;
        return [
            ...(mapSets && mapSets.items.map(x => ({ key: x.id, value: x.name }))) || [],
            ...(distibutor && distibutor.availableMapSets && distibutor.availableMapSets.map(x => ({ key: x.id, value: x.name }))) || [],
        ].filter(x => x).sort((a, b) => a.value.localeCompare(b.value));
    }

    async getEmailProviders(vendor: VendorResponse, distibutor: DistributorResponse): Promise<{ key: string, value: string }[]> {
        let result: IdNameState[];

        if (this.readonly) {
            // In readonly mode, just show the available emailProviders, nothing to load, everything the user needs to see is in this data
            result = [...vendor.availableEmailProviders];
        } else {
            // In edit mode, load the distributor's emailProviders for the user to select from
            const emailProviders = await this.admin.getCached(`listEmailProviders/${vendor.owner.id}/0/100/name/state=active`, api => api.entities.listEmailProviders(vendor.owner.id, 0, 100, 'name', 'state=active'));

            result = [
                ...(distibutor?.availableEmailProviders || []),
                ...(emailProviders?.items || []),
            ];
        }

        return uniqBy(result, 'id').map(x => {
            if (x.state && x.state !== 'active') {
                const translationKey = `STATES.${x.state.toUpperCase()}`;
                return { key: x.id, value: `${x.name} (${this.admin.translate(translationKey)})` };
            }

            return { key: x.id, value: x.name };
        }).sort((a, b) => a.value.localeCompare(b.value));
    }

}
